import { AxiosError, HttpStatusCode } from 'axios';
import { action, computed, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';
import { makePersistable } from 'mobx-persist-store';
import { TFunction } from 'i18next';
import {
  ChangePasswordResquest,
  DispatcherRequest,
  ForgottenPasswordRequest,
  SignInRequest,
} from './Payloads/requests';
import {
  AppModulesIDs,
  DispatcherResponse,
  ForgottenPasswordResponse,
  GetCurrentUserOrganizationalUnitsResponse,
  GetUserParameterValuesResponse,
  Modules,
  SignInResponse,
  SignOutResponse,
} from './Payloads/responses';
import RootStore from './../index';
import { LoginAttemptResult } from './types';
import { FEATURE_010 } from './const';
import { BaseResponse } from '40.quickConnect.DataAccess/axios/baseResponse';
import {
  API_DEV_DISPATCHER,
  API_GET_CURRENT_USER_AVAILABLE_ORGANIZATIONAL_UNIT_LIST,
  API_HOST,
  API_PROD_DISPATCHER,
  API_SIGNIN,
  API_SIGNOUT,
  API_AVAILABLE_MODULES,
  PILOT_CLIENT_WEB,
  USE_PILOT_CLIENT_WEB,
  USER_CHANGE_PASSWORD,
  USER_FORGET_PASSWORD,
  USER_GET_PARAMETER_VALUES,
} from '40.quickConnect.DataAccess/axios/apiRoutes';
import { IdentityProviders, ResponseStatus } from '90.quickConnect.Models/enums';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { MsalInfos } from '90.quickConnect.Models/models/user/msalConfig';
import { ForgottenPasswordMethodEnum } from '10.quickConnect.app/components/domain/Login/types';
import IClientHTTP from '40.quickConnect.DataAccess/ClientHTTP/interface';
import buildApiUrl from '40.quickConnect.DataAccess/axios/buildApiUrl';
import { NamedUserParameterValue } from '90.quickConnect.Models/models';
import { errorHandler } from '80.quickConnect.Core/helpers';
import { OrganizationalUnitViewer } from '90.quickConnect.Models/models/user/organizationalUnitViewer';
import mapOrganizationnalUnits from '90.quickConnect.Models/mappings/organizationnalUnits/mapOrganizationnalUnit';
import { OrganizationalUnitTree } from '80.quickConnect.Core/helpers/organizationalUnits/organizationalUnitTree';
import { isSameOuCode } from '80.quickConnect.Core/helpers/organizationalUnits/ouCode';
import { APP_VERSION } from 'const';

const defaultSignInResponse: SignInResponse = {
  status: ResponseStatus.Pending,
  authenticationMethod: 1,
  availableModules: {},
  message: '',
  errorCode: '',
  userUPN: '',
  isLoggedIn: false,
  firstName: '',
  lastName: '',
  job: '',
  organizationalUnitName: '',
  organizationalUnitId: '',
  organizationalUnitCode: '',
  phone: '',
  email: '',
  emailSupport: '',
  rights: 0,
  customerName: '',
  providerSettings: {
    instance: '',
    domain: '',
    tenantId: '',
    authority: '',
    iOS: {
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      callbackPath: '',
    },
    android: {
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      callbackPath: '',
    },
    web: {
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      callbackPath: '',
    },
    spa: {
      clientId: '',
      clientSecret: '',
      redirectUri: '',
      callbackPath: '',
    },
  },
  subscribedFeatures: [],
  userParameterValue: [],
  visiblesOrganizationalUnits: [],
  module: AppModulesIDs.EMPTY_ID,
};

class LoginStore {
  // Tag
  private static readonly TAG = '30.quickConnect.Stores/RootStore/LoginStore/index.ts';

  clientHTTP: IClientHTTP;

  signInInfos: SignInResponse = defaultSignInResponse;

  connected = false;

  authenticationMethod: IdentityProviders = IdentityProviders.AzureActiveDirectory;

  isUserInactive = false;

  availableModules: Modules | undefined = undefined;

  isLogging = false;

  // Dans le cas d'une connexion SSO avec l'AAD en utilisant la redirection
  connectByMsal = false;

  // Utilisation du RootStore afin d'appeler les Stores d'identification (LoginMsalStore, etc...);
  RootStore: RootStore;

  isLoggingOut = false;

  logger: CustomLogger;

  resetCodeLoader = false;

  urlToUse: string | undefined = API_HOST;

  currentOuCode = '';

  lastSetOuCode = '';

  userOrganizationnalUnits: OrganizationalUnitViewer[] = [];

  constructor(rootStore: RootStore, logger: CustomLogger, storageKey: string) {
    this.logger = logger;
    this.RootStore = rootStore;
    this.clientHTTP = rootStore.clientHTTP;

    makeAutoObservable(
      this,
      {
        signInInfos: observable,
        authenticationMethod: observable,
        isUserInactive: observable,
        availableModules: observable,
        isLogging: observable,
        isLoggingOut: observable,
        connected: observable,
        connectByMsal: observable,
        urlToUse: observable,
        resetCodeLoader: observable,
        currentOuCode: observable,
        lastSetOuCode: observable,
        userOrganizationnalUnits: observable,
        // Lu aussi hors des composants (contrôle d'UO) : gardé en cache tant que les UO ne changent pas
        ouTree: computed({ keepAlive: true }),
        getAvailableModules: observable,
        resetStore: action,
        logInAsync: action,
        logOutAsync: action,
        setConnectByMsal: action,
        setSignInInfos: action,
        setAuthenticationMethod: action,
        setIsUserInactive: action,
        setAvailableModules: action,
        setEmailSupport: action,
        setIsLogging: action,
        setIsLoggingOut: action,
        setUrlToUse: action,
        setResetCodeLoader: action,
        checkForFeature: action,
        setCurrentOuCode: action,
        resetCurrentOuIfNotAvailable: action,
        restoreLoggedUserContextFromStorage: action,
        syncLoggerUserContext: action,
        updateAvailableModules: action,
      },
      { autoBind: true },
    );
    // Le contexte utilisateur des traces ne vit qu'en memoire alors que la session, elle, est
    // persistee: un rechargement d'onglet, une reouverture de la PWA ou l'ouverture d'un second
    // onglet repartent donc d'un logger vide. L'hydratation de mobx-persist-store etant
    // asynchrone, on relit le stockage de maniere synchrone pour que meme les traces emises
    // pendant l'amorcage portent l'UPN.
    this.restoreLoggedUserContextFromStorage(storageKey);

    void makePersistable(this, {
      name: storageKey,
      properties: [
        'signInInfos',
        'connected',
        'connectByMsal',
        'urlToUse',
        'userOrganizationnalUnits',
        'lastSetOuCode',
        'availableModules',
      ],
      storage: window.localStorage,
    }).then(() => {
      this.setCurrentOuCode(this.lastSetOuCode); // charger le dernier code OU dans le uo code courant
      // Filet de securite si la relecture synchrone n'a rien donne (stockage indisponible,
      // format inattendu): l'hydratation fait foi.
      this.syncLoggerUserContext();
    });
    void makePersistable(this, {
      name: `${storageKey}-Session`,
      properties: ['currentOuCode', 'availableModules'],
      storage: window.sessionStorage,
    });
  }

  /**
   * Relit de maniere synchrone la session persistee pour repositionner l'UPN dans le contexte des
   * traces, avant meme la fin de l'hydratation (asynchrone) de mobx-persist-store.
   *
   * @param storageKey La cle de stockage utilisee par makePersistable.
   */
  restoreLoggedUserContextFromStorage = (storageKey: string): void => {
    try {
      const rawPersistedStore = window.localStorage.getItem(storageKey);

      if (!rawPersistedStore) return;

      const persistedStore = JSON.parse(rawPersistedStore) as Partial<Pick<LoginStore, 'signInInfos'>>;
      const persistedUserUPN = persistedStore?.signInInfos?.userUPN;

      if (persistedUserUPN) this.logger.setLoggedUserContext(persistedUserUPN);
    } catch (error) {
      // Une session persistee illisible ne doit pas empecher le demarrage: on trace l'anomalie
      // et on repart d'un contexte vide.
      errorHandler(LoginStore.TAG, error, 'restoreLoggedUserContextFromStorage', 'warn');
    }
  };

  /**
   * Aligne l'utilisateur du contexte des traces sur la session courante.
   *
   * Le contexte n'est jamais purge ici: la fin de session est explicite (resetUserContext dans
   * logOutAsync) et les traces d'un echec de connexion doivent conserver l'UPN tente malgre le
   * resetStore declenche dans le catch.
   */
  syncLoggerUserContext = (): void => {
    const { userUPN } = this.signInInfos;

    if (userUPN && userUPN !== this.logger.userUPN) this.logger.setLoggedUserContext(userUPN);
  };

  resetStore = () => {
    this.setSignInInfos(defaultSignInResponse);
    this.setIsLogging(false);
    this.setIsLoggingOut(false);
    this.setAuthenticationMethod(1);
    this.setIsUserInactive(false);
    this.setConnectByMsal(false);
    this.setUrlToUse(API_HOST);
    this.setResetCodeLoader(false);
    this.setCurrentOuCode('');
    this.setOrganizationalUnits([]);
  };

  checkForFeature = (feature: string): boolean => {
    return this.signInInfos.subscribedFeatures[feature];
  };

  /** Feature multi-UO souscrite : active le contrôle d'UO des formulaires, brouillons, duplications et inbox */
  isMultiOUFeatureEnabled = (): boolean => !!this.checkForFeature(FEATURE_010);

  /** L'utilisateur peut changer d'UO : feature souscrite et au moins 2 UO distinctes (UO de rattachement incluse) */
  isMultiOUUser = (): boolean => this.isMultiOUFeatureEnabled() && this.ouTree.getOuCount() >= 2;

  /** Arbre des UO accessibles à l'utilisateur : UO de visibilité et UO de rattachement, dédoublonnées */
  get ouTree(): OrganizationalUnitTree {
    const { organizationalUnitId, organizationalUnitCode, organizationalUnitName } = this.signInInfos;
    return OrganizationalUnitTree.build(
      { id: organizationalUnitId ?? '', code: organizationalUnitCode ?? '', name: organizationalUnitName ?? '' },
      this.userOrganizationnalUnits,
    );
  }

  /** UO effective : UO courante, à défaut UO de rattachement */
  get effectiveOuCode(): string {
    return this.currentOuCode || this.signInInfos.organizationalUnitCode;
  }

  /**
   * UO portée par une déclaration enregistrée maintenant : UO effective avec le multi-UO, sinon aucune (le serveur
   * applique l'UO de rattachement)
   */
  currentDeclarationOuCode = (): string | undefined =>
    this.isMultiOUFeatureEnabled() ? this.effectiveOuCode || undefined : undefined;

  /**
   * UO dont le logo est affiché : avec le multi-UO, l'UO courante ou son plus proche ancêtre ayant un logo (sans
   * dépasser l'UO de rattachement), sinon l'UO de rattachement si elle a un logo
   */
  get logoOwner(): OrganizationalUnitViewer | undefined {
    const tree = this.ouTree;
    const currentOuLogoOwner =
      this.isMultiOUFeatureEnabled() && this.currentOuCode ? tree.findLogoOwner(this.currentOuCode) : undefined;
    if (currentOuLogoOwner) return currentOuLogoOwner;

    const attachedOu = tree.getNode(this.signInInfos.organizationalUnitCode);
    return attachedOu?.logo ? attachedOu : undefined;
  }

  setCurrentOuCode = (ouCode: string) => {
    this.currentOuCode = ouCode;
    this.lastSetOuCode = ouCode;
  };

  setOrganizationalUnits = (ous: OrganizationalUnitViewer[]) => {
    this.userOrganizationnalUnits = ous;
  };

  setConnectByMsal = async (isMsalRedirecting: boolean) => (this.connectByMsal = isMsalRedirecting);

  setResetCodeLoader = (flag: boolean) => {
    this.resetCodeLoader = flag;
  };

  setIsLogging = (isLogging: boolean) => (this.isLogging = isLogging);

  setIsLoggingOut = (isLoggingOut: boolean) => (this.isLoggingOut = isLoggingOut);

  setSignInInfos = (signInInfos: SignInResponse) => {
    this.signInInfos = signInInfos;
    this.connected = signInInfos.isLoggedIn;
    // A chaque connexion, l'UO courante repart sur l'UO de rattachement (ajoutée à l'arbre des UO par ouTree)
    this.setCurrentOuCode(signInInfos.organizationalUnitCode);
    this.setOrganizationalUnits(signInInfos.visiblesOrganizationalUnits ?? []);
    this.setAvailableModules(signInInfos.availableModules);
    // Tout etablissement de session passe par ici, connexion classique comme retour de
    // redirection Entra ID (LoginMsalStore.validateTokenAsync): c'est le point unique ou le
    // contexte des traces est aligne sur l'utilisateur connecte.
    this.syncLoggerUserContext();
  };

  getAvailableModules(): Modules {
    return this.availableModules || {};
  }

  /**
   * Permet de reset certaines valeurs du login afin de rediriger vers le login lorsqu'une réponse 401 arrive
   *
   * @param {boolean} isConnected
   * @memberof LoginStore
   */
  setIsConnectedFor401HTTPCode = (isConnected: boolean): void => {
    this.signInInfos.isLoggedIn = isConnected;
    this.connected = isConnected;
    this.connectByMsal = isConnected;
  };

  setUrlToUse = (url: string | undefined) => {
    this.urlToUse = url;
  };

  setAuthenticationMethod = (authenticationMethod: IdentityProviders) =>
    (this.authenticationMethod = authenticationMethod);

  setIsUserInactive = (isUserInactive: boolean) => (this.isUserInactive = isUserInactive);

  setCurrentModule = (module: AppModulesIDs) => {
    this.signInInfos.module = module;
  };

  setAvailableModules = (availableModules: Modules | undefined) => (this.availableModules = availableModules || {});

  setEmailSupport = (emailSupport: string | undefined) => (this.signInInfos.emailSupport = emailSupport || '');

  verifyFieldUpnAndCgu = ({ userUPN, cgu }: SignInRequest, t: TFunction): boolean => {
    if (userUPN.trim() === '') {
      toast.error(t('qcapp_warning_no_login_entered').toString());
      return false;
    }

    if (!cgu) {
      toast.error(t('qcapp_login_privacy_policy_not_accepted').toString());
      return false;
    }

    return true;
  };

  logInAsync = async (request: SignInRequest, t: TFunction): Promise<LoginAttemptResult> => {
    this.logger.resetInitDateTimeApp();

    /*
     * Les traces de cette tentative de connexion doivent porter
     * l'UPN réellement soumis.
     */
    this.logger.setLoggedUserContext(request.userUPN);
    this.setIsLogging(true);

    try {
      const response = await this.clientHTTP.post<SignInResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(API_SIGNIN),
        request,
        {
          withCredentials: true,

          /*
           * Sur signin, 401 et 404 font partie du protocole
           * d'authentification.
           *
           * Le détail est traité ci-dessous.
           */
          expectedStatuses: [401, 404],
        },
      );

      if (200 <= response.status && response.status < 300) {
        /*
         * Authentification Azure Active Directory.
         */
        if (response.data?.authenticationMethod === IdentityProviders.AzureActiveDirectory && !this.connectByMsal) {
          this.logger.info(
            LoginStore.TAG,
            `[Client Web] - LoginStore.index.ts, logInAsync method: début du traitement de redirection MSAL pour l'utilisateur ${request.userUPN}.`,
          );

          this.setConnectByMsal(true);

          const {
            providerSettings: { spa, instance, domain, tenantId, authority },
            userUPN,
          } = response.data;

          const msalInfos: MsalInfos = {
            instance,
            domain,
            tenantId,
            authority,
            spa,
            userUPN,
          };

          this.RootStore.LoginMsalStore.redirectLogin(msalInfos);

          return LoginAttemptResult.ExternalAuthentication;
        }

        /*
         * Vérification de l'accès au Client Web Pilot.
         */
        if (USE_PILOT_CLIENT_WEB === 'true') {
          if (response.data.userParameterValue?.length > 0) {
            const findClientPilot = response.data.userParameterValue.find((item) => item.name === PILOT_CLIENT_WEB);

            if (findClientPilot?.value === 'true') {
              this.setSignInInfos(response.data);

              this.logger.info(
                LoginStore.TAG,
                `[Client Web] - LoginStore index.ts logInAsync method: l'utilisateur ${
                  request.userUPN
                } est authentifié le ${new Date().toISOString()}`,
              );

              return LoginAttemptResult.Authenticated;
            }
          }

          toast.error(t('pilotClienWeb').toString());

          return LoginAttemptResult.Failed;
        }

        /*
         * Connexion QuickConnect réussie.
         */
        this.setSignInInfos(response.data);

        this.logger.info(
          LoginStore.TAG,
          `[Client Web] - LoginStore index.ts logInAsync method: l'utilisateur ${
            request.userUPN
          } est authentifié le ${new Date().toISOString()}`,
        );

        return LoginAttemptResult.Authenticated;
      }

      /*
       * Cas défensif : réponse HTTP non 2xx qui ne serait pas
       * passée par le mécanisme d'erreur du ClientHTTP.
       */
      const error = new Error(
        `réponse du serveur KO pour la connexion : ${response.data.status} pour l'utilisateur ${request.userUPN}: Message: ${response.data.message}`,
      );

      errorHandler(LoginStore.TAG, error, 'logInAsync');

      return LoginAttemptResult.Failed;
    } catch (error: unknown) {
      const errorAxios = error as AxiosError<SignInResponse>;

      const status = errorAxios?.response?.status;

      const data = errorAxios?.response?.data;

      const errorCode = data?.errorCode;

      /*
       * On reset uniquement l'état UI lié à l'inactivité.
       */
      this.setIsUserInactive(false);

      /*
       * Si l'API a reconnu un utilisateur QuickConnect,
       * on conserve les informations nécessaires à la suite
       * du processus : méthode, modules, support...
       */
      if (status !== 404 && data?.authenticationMethod === IdentityProviders.QuickConnect) {
        this.setAuthenticationMethod(data.authenticationMethod);

        this.setAvailableModules(data.availableModules);

        this.setEmailSupport(data.emailSupport);
      } else {
        this.resetStore();
      }

      /**
       * Utilisateur inactif
       */
      if (status === 401 && errorCode === 'MsgWarning_CannotLogin_InactiveUser') {
        this.setIsUserInactive(true);

        const errorMsg = data?.message ?? errorAxios.message;

        this.logger.warn(LoginStore.TAG, `[Client Web] - logInAsync: inactive user (${request.userUPN})`);

        toast.error(
          t('logInAsync_error_withMsg', {
            msg: errorMsg,
          }).toString(),
        );

        return LoginAttemptResult.Failed;
      }

      /**
       * Utilisateur inconnu
       */
      if (status === 404 && errorCode === 'MSG_SQL_USERNOTFOUND') {
        errorAxios.message = `Utilisateur non trouvé: ${request.userUPN}`;

        errorHandler(LoginStore.TAG, errorAxios, 'logInAsync');

        toast.error(t('logInAsync_userNotFound').toString());

        return LoginAttemptResult.Failed;
      }

      /*
       * Mauvais mot de passe.
       *
       * L'UI reste volontairement à l'étape Credentials afin
       * que l'utilisateur puisse simplement corriger son mot
       * de passe.
       */
      if (status === 401 && errorCode === 'MsgWarning_CannotLogin_WrongPassword') {
        const errorMsg = errorAxios.response ? `${errorAxios.response.data?.message}` : errorAxios.message;

        errorAxios.message =
          `Mauvais mot de passe! Statut: ${errorAxios.response?.data.status}` +
          ` - Message: ${errorAxios.response?.data.message}`;

        errorHandler(LoginStore.TAG, errorAxios, 'logInAsync');

        toast.error(
          t('logInAsync_error_withMsg', {
            msg: errorMsg,
          }).toString(),
        );

        return LoginAttemptResult.Failed;
      }

      /*
       * L'identifiant QuickConnect a été reconnu,
       * mais aucun mot de passe n'a encore été fourni.
       *
       * C'est la seule réponse qui autorise l'UI à passer
       * de l'étape Identifier à l'étape Credentials.
       */
      if (status === 401 && errorCode === 'MsgWarning_CannotLogin_NoPassword') {
        return LoginAttemptResult.PasswordRequired;
      }

      /*
       * Autres erreurs d'authentification / techniques.
       */
      const errorMsg = data?.message ?? errorAxios.message;

      errorHandler(LoginStore.TAG, errorAxios, 'logInAsync');

      toast.error(
        t('logInAsync_error_withMsg', {
          msg: errorMsg,
        }).toString(),
      );

      return LoginAttemptResult.Failed;
    } finally {
      this.setIsLogging(false);
    }
  };

  logOutAsync = async (t: TFunction): Promise<void> => {
    const { userUPN } = this.signInInfos;
    this.logger.info(
      LoginStore.TAG,
      `[Client Web] - LoginStore index.ts logOutAsync method: Début de la déconnexion de l'utilisateur ${userUPN}`,
    );
    // Vérification si l'utilisateur s'est connecté par l'AAD
    if (this.connectByMsal) {
      // On reset certains observables.
      this.RootStore.FormStore.MainMenuStore.reset();
      // On doit loggout l'utilisateur en faisant appel à Microsoft
      await this.RootStore.LoginMsalStore.logoutMsalAsync();
      this.setConnectByMsal(false);
      // Fin de session: on purge l'utilisateur et le formulaire du contexte des traces.
      this.logger.resetUserContext();
    } else {
      this.setIsLoggingOut(true);
      this.clientHTTP
        .post<SignOutResponse>(
          this.RootStore.CommonStore.chooseBaseUrl(API_SIGNOUT),
          {},
          {
            withCredentials: true,
          },
        )
        .then(async (response) => {
          if (200 <= response.status && response.status < 300) {
            this.setAuthenticationMethod(IdentityProviders.AzureActiveDirectory);
            await this.logger.info(
              LoginStore.TAG,
              `[Client Web] - LoginStore index.ts logOutAsync method: réponse du serveur ok pour la déconnexion: ${response.status} pour l'utilisateur ${userUPN}`,
            );
          } else
            throw new Error(
              `réponse du serveur KO pour la déconnexion : ${response.data.status} pour l'utilisateur ${userUPN} - Message: ${response.data.message}`,
            );
        })
        .catch((error) => {
          if (error instanceof AxiosError) {
            const errorAxios = error as AxiosError<SignOutResponse>;
            const errorMsg = errorAxios?.response ? `${errorAxios?.response?.data?.message}` : errorAxios?.message;

            errorHandler(LoginStore.TAG, errorAxios, 'logOutAsync');

            toast.error(
              t('logOutAsync_errorWithMsg', {
                msg: errorMsg,
              }).toString(),
            );
          }
        })
        .finally(async () => {
          this.setSignInInfos(defaultSignInResponse);
          this.setIsLoggingOut(false);
          // Supression des données URL enregistrées par le SessionStore
          this.RootStore.SessionStore.resetStore();
          // Fin de session: on purge l'utilisateur et le formulaire du contexte des traces.
          this.logger.resetUserContext();
          if (!this.signInInfos)
            await this.logger.info(
              LoginStore.TAG,
              `[Client Web] - LoginStore index.ts logOutAsync method: fin d'execution de la méthode pour l'utilisateur ${userUPN}`,
            );
        });
    }
  };

  dispatcherAsync = async (request: DispatcherRequest): Promise<DispatcherResponse | undefined> => {
    try {
      this.logger.resetInitDateTimeApp();
      this.setIsLogging(true);
      const { REACT_APP_ENV, REACT_APP_DEV_KEY_DISPATCHER, REACT_APP_PROD_KEY_DISPATCHER } = process.env;
      const url =
        REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'int' || REACT_APP_ENV === 'demo'
          ? API_DEV_DISPATCHER
          : API_PROD_DISPATCHER;
      const key =
        REACT_APP_ENV === 'dev' || REACT_APP_ENV === 'int' || REACT_APP_ENV === 'demo'
          ? REACT_APP_DEV_KEY_DISPATCHER
          : REACT_APP_PROD_KEY_DISPATCHER;
      const { data } = await this.clientHTTP.post<DispatcherResponse>(`${url}`, request, {
        headers: { 'x-mobile-dispatcher-key': key ?? '' },

        /*
         * Un compte inconnu du dispatcher (404) fait partie du protocole: c'est a cette methode,
         * et non au client HTTP generique, de decider comment le tracer et le traiter.
         */
        expectedStatuses: [HttpStatusCode.NotFound],
      });
      if (data) {
        return data;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosErr = error as AxiosError<DispatcherResponse>;

        axiosErr.message = `[Client Web] DeclarationStore file - dispatcherAsync method: Une erreur est survenue lors de la récupération de l'url par le dispatcher pour cet userUPN ${request.userUpn} : ${axiosErr?.response?.data.status}  ${axiosErr?.response?.data.message}`;
        errorHandler(LoginStore.TAG, error, 'dispatcherAsync');

        // Compte inconnu du dispatcher: on relance pour que l'appelant (Login/hooks.ts connect())
        // affiche le toast déjà prévu à cet effet pour ce cas, jusqu'ici jamais atteint puisque
        // cette méthode n'avait jamais relancé l'erreur.
        if (axiosErr.response?.status === HttpStatusCode.NotFound) {
          throw axiosErr;
        }
      } else if (error instanceof Error) {
        error.message = `Une erreur est survenue lors de la récupération de l'url par le dispatcher pour cet userUPN ${request.userUpn} : ${error.message}`;
        errorHandler(LoginStore.TAG, error, 'dispatcherAsync');
      }
    } finally {
      this.setIsLogging(false);
    }
  };

  getAvailableMethods = async (userUpn: string) => {
    this.setResetCodeLoader(true);
    try {
      const { data } = await this.clientHTTP.post<ForgottenPasswordResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(USER_FORGET_PASSWORD),
        {
          userUpn,
          appVersion: APP_VERSION,
          method: ForgottenPasswordMethodEnum.Unknown,
          supportsAdministratorMethod: true,
        },
      );
      if (data) {
        this.setResetCodeLoader(false);
        return data;
      }
    } catch (e) {
      const errorAxios = e as AxiosError<any>;
      this.setResetCodeLoader(false);
      toast.error(errorAxios?.response?.data?.message);
    }
  };

  getResetCode = async ({ userUpn, method }: ForgottenPasswordRequest, t: TFunction) => {
    try {
      this.setResetCodeLoader(true);
      const { data } = await this.clientHTTP.post<ForgottenPasswordResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(USER_FORGET_PASSWORD),
        {
          userUpn,
          appVersion: APP_VERSION,
          method,
          supportsAdministratorMethod: true,
        },
      );

      if (data) {
        this.setResetCodeLoader(false);
        return data;
      }
    } catch (error) {
      this.setResetCodeLoader(false);
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<any>;
        if (errorAxios?.response?.status === 403) {
          toast.error(errorAxios?.response?.data?.message, { autoClose: 10000 });
        } else {
          toast.error(t('forget_password.reset_password_error').toString() + errorAxios?.response?.data?.message, {
            autoClose: 10000,
          });
        }
      }
    }
  };

  sendResetCode = async ({ userUpn, newPassword, resetCode }: ForgottenPasswordRequest, t: TFunction) => {
    try {
      this.setResetCodeLoader(true);
      const { data } = await this.clientHTTP.post<ForgottenPasswordResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(USER_FORGET_PASSWORD),
        {
          userUpn,
          appVersion: APP_VERSION,
          method: ForgottenPasswordMethodEnum.Force,
          newPassword,
          resetCode,
          supportsAdministratorMethod: true,
        },
      );

      if (data) {
        this.setResetCodeLoader(false);
        toast.success(t('forget_password.reset_password_success').toString());
        return data;
      }
    } catch (e) {
      const errorAxios = e as AxiosError<any>;
      this.setResetCodeLoader(false);
      toast.error(errorAxios?.response?.data?.message);
    }
  };

  changePassword = async ({ userUPN, newPassword, currentPassword }: ChangePasswordResquest, t: TFunction) => {
    try {
      const { data } = await this.clientHTTP.post(
        this.RootStore.CommonStore.chooseBaseUrl(USER_CHANGE_PASSWORD),
        {
          userUPN,
          newPassword,
          currentPassword,
        },
        {
          withCredentials: true,
        },
      );

      if (data) {
        toast.success(t('qcapp_password_updated').toString());
        return data;
      }
    } catch (e) {
      toast.error(t("The user's password has not been changed").toString());
    }
  };

  async getUserParameterValuesAsync(): Promise<NamedUserParameterValue[]> {
    try {
      const getUserParameterValuesUrl = RootStore.getInstance().CommonStore.chooseBaseUrl(
        buildApiUrl(USER_GET_PARAMETER_VALUES, { forThisUPN: this.signInInfos.userUPN }),
      );
      const {
        data: { status, message, userParameterValues },
      } = await this.clientHTTP.get<GetUserParameterValuesResponse>(getUserParameterValuesUrl, {
        withCredentials: true,
      });

      if (status !== ResponseStatus.OK) throw new Error(`Statut de réponse NO OK: ${message}`);

      return userParameterValues;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<GetUserParameterValuesResponse>;
        errorAxios.message = `Les infos paramètres utilisateur n'ont pas être récupérées. Statut: ${errorAxios.response?.data.status} - Message: ${errorAxios.response?.data.message}`;
        errorHandler(LoginStore.TAG, error, 'getUserParameterValuesAsync');
      } else if (error instanceof Error) {
        error.message = `Les infos paramètres utilisateur n'ont pas être récupérées. Message: ${error.message}`;
        errorHandler(LoginStore.TAG, error, 'getUserParameterValuesAsync');
      }
    }
    return [];
  }

  /**
   * Après le rechargement des UO de l'utilisateur : si l'UO courante n'est plus accessible (UO retirée de ses UO de
   * visibilité) ou si l'utilisateur n'est plus multi-UO, retour sur l'UO de rattachement
   */
  resetCurrentOuIfNotAvailable = (): void => {
    const attachedOuCode = this.signInInfos.organizationalUnitCode;
    if (!attachedOuCode || isSameOuCode(this.currentOuCode, attachedOuCode)) return;
    if (this.isMultiOUUser() && this.ouTree.contains(this.currentOuCode)) return;

    this.logger.info(
      LoginStore.TAG,
      `UO courante "${this.currentOuCode}" plus accessible : retour sur "${attachedOuCode}"`,
    );
    this.setCurrentOuCode(attachedOuCode);
  };

  getUserOrganizationnalUnits = async (): Promise<void> => {
    try {
      const getUserOrganizationnalUnitsUrl = RootStore.getInstance().CommonStore.chooseBaseUrl(
        `${API_GET_CURRENT_USER_AVAILABLE_ORGANIZATIONAL_UNIT_LIST}`,
      );
      const {
        data: { status, message, organizationalUnits },
      } = await this.clientHTTP.get<GetCurrentUserOrganizationalUnitsResponse>(getUserOrganizationnalUnitsUrl, {
        withCredentials: true,
      });

      if (status !== ResponseStatus.OK) throw new Error(`Statut de réponse NO OK: ${message}`);

      if (organizationalUnits && organizationalUnits.length > 0) {
        const mappedOus = organizationalUnits.map(mapOrganizationnalUnits);
        this.setOrganizationalUnits(mappedOus);
        this.resetCurrentOuIfNotAvailable();
      }
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<GetUserParameterValuesResponse>;
        errorAxios.message = `Les unités organisationnelles n'ont pas être récupérées. Statut: ${errorAxios.response?.data.status} - Message: ${errorAxios.response?.data.message}`;
        errorHandler(LoginStore.TAG, error, 'getUserOrganizationnalUnits');
      } else if (error instanceof Error) {
        error.message = `Les unités organisationnelles n'ont pas être récupérées. Message: ${error.message}`;
        errorHandler(LoginStore.TAG, error, 'getUserOrganizationnalUnits');
      }
    }
  };

  async updateAvailableModules(): Promise<Modules> {
    try {
      const body = {};
      const response = await this.clientHTTP.get<BaseResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(API_AVAILABLE_MODULES),
        {
          withCredentials: true,
        },
      );
      if (200 <= response.status && response.status <= 299) {
        if (response) {
          const { availableModules } = response.data as SignInResponse;

          if (availableModules && typeof availableModules === 'object') {
            this.setAvailableModules(availableModules); // mise à jour du store
            return availableModules; // on retourne ce qui a été défini
          }
          return {};
        }
        return {};
      } else {
        const error = new Error(
          `Une erreur est survenue lors de la demande de modules: Statut: ${response.data.status}, Message: ${response.data.message}`,
        );
        errorHandler(LoginStore.TAG, error, 'getAvailableModules', 'error');
        return {};
      }
    } catch (error) {
      //TODO KTY à retirer quand l'api sera faite ne fait pas parti des mock data
      if (process.env.REACT_APP_QC_WORK_SITE_MOCK_DATA === 'true') {
        return this?.getAvailableModules() || {};
      }
      errorHandler(LoginStore.TAG, error, 'getAvailableModules');
      return {};
    }
  }
}

export default LoginStore;
